// Which flashcards are due, for the start page, the Island and the review screen. Numbers only:
// this never loads the scheduler, so it can sit in the main bundle. The chip and the review screen
// both build their list here, so "Review now" never opens with nothing to show.
import { dateKey } from "./activity.js";

const H4 = 4 * 3600e3;
// Learning steps up to 20 minutes away count as ready (Anki's learn-ahead).
export const AHEAD = 20 * 60e3;
const fin = (v) => typeof v === "number" && isFinite(v);

// The study day starts at 4 am, so a 12:30 am cram still belongs to the evening before.
export const studyDay = (ms) => dateKey(new Date(ms - H4));
// 4 am local on the day after this study day (ms).
export function dayEnd(ms) { const d = new Date(ms - H4); return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 4).getTime(); }
export const dayStart = (ms) => { const d = new Date(ms - H4); return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 4).getTime(); };

// One stored schedule entry, or null for New. Anything malformed counts as New.
// { k: 0, f } is a New card moved to the front by "Not yet" in the quick sort.
export function entry(s) {
	if (!s || typeof s !== "object") return null;
	if (s.k === 0) return fin(s.f) ? { k: 0, f: s.f } : null;
	if (s.k !== 1 && s.k !== 2 && s.k !== 3) return null;
	for (const f of ["due", "st", "df", "sd", "r", "l", "ls", "lr"]) if (!fin(s[f])) return null;
	return s;
}

export function opts(db) {
	const o = (db && db.opts) || {};
	return {
		ret: o.ret === 0.85 || o.ret === 0.95 ? o.ret : 0.9,
		newPerDay: Number.isInteger(o.newPerDay) && o.newPerDay >= 0 && o.newPerDay <= 999 ? o.newPerDay : 20,
		mix: o.mix !== false,
	};
}

// New cards first seen this study day. Quick-sort entries (q) are not studying, so they don't count.
export function newDoneToday(log, now) {
	const a = dayStart(now), b = dayEnd(now);
	let n = 0;
	for (const e of Array.isArray(log) ? log : []) if (Array.isArray(e) && e[3] === 0 && !e[4] && e[1] >= a && e[1] < b) n++;
	return n;
}

// The review queue: started cards due by the end of the study day (soonest first), then New cards
// up to what is left of today's pace, oldest-added first ("Not yet" cards ahead of them).
// only: one deck id. Items are { id, deck, due?, sd? } (no due = New).
export function queue(db, sched, log, now, only) {
	const c = (sched && sched.c && typeof sched.c === "object") ? sched.c : {};
	const end = dayEnd(now), o = opts(db);
	const due = [], front = [], fresh = [];
	const decks = db && Array.isArray(db.decks) ? db.decks : [];
	decks.forEach((d, di) => {
		if (!d || !Array.isArray(d.cards) || (only && d.id !== only)) return;
		const mine = [];
		for (const card of d.cards) {
			if (!card || typeof card.id !== "string") continue;
			const e = entry(c[card.id]);
			if (e && e.k) { if (e.due <= end) due.push({ id: card.id, deck: d.id, due: e.due, sd: e.sd, di }); }
			else if (e) front.push({ id: card.id, deck: d.id, f: e.f, di });
			else mine.push({ id: card.id, deck: d.id, di });
		}
		fresh.push(mine);
	});
	due.sort(o.mix ? (a, b) => a.due - b.due : (a, b) => a.di - b.di || a.due - b.due);
	front.sort((a, b) => a.f - b.f);
	// Mixed: one New card from each deck in turn. Otherwise deck by deck.
	const news = [];
	if (o.mix) for (let i = 0, more = true; more; i++) { more = false; for (const m of fresh) if (i < m.length) { news.push(m[i]); more = true; } }
	else for (const m of fresh) news.push(...m);
	const room = Math.max(0, o.newPerDay - newDoneToday(log, now));
	return due.concat(front.concat(news).slice(0, room));
}

// The length of queue(), without building it (this part is in the main bundle).
export function dueCount(db, sched, log, now) {
	const c = (sched && sched.c) || {}, end = dayEnd(now);
	let started = 0, fresh = 0;
	for (const d of db && Array.isArray(db.decks) ? db.decks : []) {
		if (!d || !Array.isArray(d.cards)) continue;
		for (const card of d.cards) {
			if (!card || typeof card.id !== "string") continue;
			const e = entry(c[card.id]);
			if (e && e.k) { if (e.due <= end) started++; } else fresh++;
		}
	}
	return started + Math.min(fresh, Math.max(0, opts(db).newPerDay - newDoneToday(log, now)));
}
export const deckDue = (db, deckId, sched, log, now) => queue(db, sched, log, now, deckId).length;

// The card to show next: anything due now, then New cards, then whatever comes soonest (a learning
// step shown a little early rather than ending the session on "1 card due"). Avoids showing the
// same card twice in a row when there is another one. lastDeck (mixed review): a ready card from
// another deck goes first, so one deck's cards don't come in a long run. mix false (deck by deck):
// the first deck in the deck list with a ready card goes first (di is the deck's place in the list).
export function nextCard(q, now, lastId, lastDeck, mix = true) {
	const pool = q.length > 1 ? q.filter((x) => x.id !== lastId) : q;
	if (!mix) {
		const decks = [...new Set(pool.slice().sort((a, b) => (a.di || 0) - (b.di || 0)).map((x) => x.deck))];
		for (const d of decks) { const hit = pick(pool.filter((x) => x.deck === d), now, true); if (hit) return hit; }
		return pick(pool, now, false);
	}
	if (lastDeck != null) { const hit = pick(pool.filter((x) => x.deck !== lastDeck), now, true); if (hit) return hit; }
	return pick(pool, now, false);
}
function pick(pool, now, readyOnly) {
	let best = null;
	for (const x of pool) if (x.due != null && (x.sd > 0 || x.due <= now) && (!best || x.due < best.due)) best = x;
	if (best) return best;
	const n = pool.find((x) => x.due == null);
	if (n || readyOnly) return n || null;
	for (const x of pool) if (!best || x.due < best.due) best = x;
	return best;
}

// Soonest due time after now, for the refresh timer.
export function nextDue(sched, now) {
	const c = (sched && sched.c) || {};
	let t = 0;
	for (const k in c) { const e = entry(c[k]); if (e && e.k && e.due > now && (!t || e.due < t)) t = e.due; }
	return t;
}
